import { CalendarClock, CalendarCheck, CalendarRange } from "lucide-react";
import { DemoOutcome, OutcomeBullet } from "@/src/data/demo-outcomes";

// ═══════════════════════════════════════════════════════════════════════
// OUTCOME TIMELINE — "what becomes possible" on demo detail pages
// ═══════════════════════════════════════════════════════════════════════
// Sits between the hero and "What's inside" on DemoDetail. The intent
// is to give a prospect a concrete picture of what changes in their
// business at Week 1 / Month 1 / Quarter 1, before they click into the
// live Operating System. The copy lives in src/data/demo-outcomes.ts so each demo
// gets its own category-specific wording.
//
// Design: three equal columns on desktop, stacked on mobile. Icons are
// intentionally understated (not cartoon time-icons) — the copy does
// the lifting. Accent color is emerald to match Explore's convention.
// ═══════════════════════════════════════════════════════════════════════

interface OutcomeTimelineProps {
  outcomes: DemoOutcome;
}

export default function OutcomeTimeline({ outcomes }: OutcomeTimelineProps) {
  return (
    <div className="w-full max-w-7xl mx-auto px-8 py-20 border-b border-slate-100">
      <div className="mb-12 max-w-3xl">
        <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-4">
          What becomes possible
        </span>
        <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-3">
          If you ran this in your business, here's the shape of what changes.
        </h2>
        <p className="text-base text-slate-600 leading-relaxed">
          Indicative tempo based on how similar operators have adopted systems of this shape. Your specifics will differ — that's what the Architect conversation calibrates.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        <OutcomeColumn
          icon={CalendarClock}
          label="Week 1"
          sublabel="First 7 days"
          bullets={outcomes.week_1}
        />
        <OutcomeColumn
          icon={CalendarCheck}
          label="Month 1"
          sublabel="After 30 days"
          bullets={outcomes.month_1}
        />
        <OutcomeColumn
          icon={CalendarRange}
          label="Quarter 1"
          sublabel="After 90 days"
          bullets={outcomes.quarter_1}
        />
      </div>
    </div>
  );
}

interface OutcomeColumnProps {
  icon: typeof CalendarClock;
  label: string;
  sublabel: string;
  bullets: OutcomeBullet[];
}

function OutcomeColumn({ icon: Icon, label, sublabel, bullets }: OutcomeColumnProps) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col gap-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <div className="text-lg font-semibold text-slate-900 leading-tight">{label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{sublabel}</div>
        </div>
      </div>

      <div className="h-px bg-slate-100" />

      <ul className="flex flex-col gap-4">
        {bullets.map((b, i) => (
          <li key={i} className="flex flex-col gap-1">
            <div className="text-sm font-semibold text-slate-900 leading-snug">
              {b.label}
            </div>
            <div className="text-[13px] text-slate-600 leading-relaxed">
              {b.detail}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
